import type { ReactNode } from 'react';
import { AppActivationPill, MonitoringStatusPill } from '@odo/shared-ui';
import { useDmCompliance } from '../../../hooks/useDmCompliance';
import { LatestScanPreview } from '@odo/shared-ui';
import { dmScansQueryConfig } from '../../../services/patient/dmScans';
import type { OverviewPatient } from '../../../services/patient';

import { ScanComplianceRing } from './ScanComplianceRing';
import { StatRing } from './StatRing';

interface TreatmentProgressCardProps {
    patient: OverviewPatient | null;
    patientId: string | null;
}

const PROGRESS_COLOR = '#10b981';

function RingSkeleton() {
    return (
        <div className="flex flex-col items-center gap-2 w-full min-w-[110px]">
            <div className="w-24 h-24 rounded-full bg-gray-100 animate-pulse" />
            <div className="h-3 w-20 bg-gray-100 animate-pulse rounded" />
            <div className="h-3 w-24 bg-gray-100 animate-pulse rounded" />
        </div>
    );
}

// Equal-width centered box so the indicators split the row evenly rather than
// clustering at their intrinsic size.
function RingSlot({ children }: { children: ReactNode }) {
    return <div className="flex-1 min-w-0 flex items-center justify-center">{children}</div>;
}

function TreatmentProgressCard({ patient, patientId }: TreatmentProgressCardProps) {
    const current = patient?.currentAligner ?? null;
    const total = patient?.totalAligners ?? null;
    const monitoringStatus = patient?.monitoringStatus ?? null;
    const dmAppActivationStatus = patient?.dmAppActivationStatus ?? null;
    const dmId = patient?.dmId ?? null;

    const hasData = current != null && total != null && total > 0;
    const pct = hasData ? Math.min(Math.round((current / total) * 100), 100) : null;

    const { data: compliance, isLoading: complianceLoading } = useDmCompliance(
        patientId ?? undefined,
        dmId
    );

    // An unlinked patient can carry a stale monitoring/app-activation status.
    const isDmLinked = dmId != null;
    const hasDmStatusPills = isDmLinked && (!!monitoringStatus || !!dmAppActivationStatus);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-full min-h-0 flex flex-col overflow-hidden gap-5">
            <div className="flex items-center justify-between gap-2 shrink-0 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    Treatment progress
                </span>
                {hasDmStatusPills && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <MonitoringStatusPill status={monitoringStatus} />
                        <AppActivationPill status={dmAppActivationStatus} />
                    </div>
                )}
            </div>

            <div className="shrink-0 flex gap-6 py-1">
                <RingSlot>
                    <StatRing
                        pct={pct}
                        color={PROGRESS_COLOR}
                        label={hasData ? `Aligner ${current}/${total}` : ''}
                        sublabel={hasData ? `${total - current} remaining` : 'No aligner data'}
                        emptyLabel="Advancement"
                    />
                </RingSlot>

                <RingSlot>
                    {isDmLinked ? (
                        complianceLoading ? (
                            <RingSkeleton />
                        ) : (
                            <ScanComplianceRing
                                lateCount={compliance?.lateCount ?? 0}
                                onTimeCount={compliance?.onTimeCount ?? 0}
                            />
                        )
                    ) : (
                        <ScanComplianceRing lateCount={0} onTimeCount={0} />
                    )}
                </RingSlot>
            </div>

            <LatestScanPreview queries={dmScansQueryConfig} patientId={patientId} dmId={dmId} />
        </div>
    );
}

export { TreatmentProgressCard };
